import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, Copy, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Tour } from '@/types/tours';
import TourForm from './TourForm';

const ToursTab: React.FC = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTour, setEditingTour] = useState<Tour | null>(null);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const pages = [
    { value: '/chat-juridico', label: 'Chat Jurídico' },
    { value: '/casos', label: 'Casos/Clientes' },
    { value: '/casos/:id', label: 'Detalhes do Caso' },
    { value: '/melhore-seu-texto', label: 'Melhore seu Texto' },
    { value: '/calculos', label: 'Calculadora Jurídica' },
    { value: '/dashboard', label: 'Dashboard' },
    { value: '/documentos', label: 'Documentos' },
  ];

  useEffect(() => {
    loadTours();
  }, []);

  useEffect(() => {
    if (tours.length > 0) {
      loadStats();
    }
  }, [tours]);

  const loadTours = async () => {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const typedTours = (data || []).map(tour => ({
        ...tour,
        steps: tour.steps as unknown as Tour['steps']
      })) as Tour[];
      
      setTours(typedTours);
    } catch (error) {
      console.error('Erro ao carregar tours:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os tours.',
        variant: 'destructive',
      });
    }
  };

  const loadStats = async () => {
    const statsData: Record<string, number> = {};
    
    for (const tour of tours) {
      const { count } = await supabase
        .from('tour_completions' as any)
        .select('*', { count: 'exact', head: true })
        .eq('tour_id', tour.id);
      
      statsData[tour.id] = count || 0;
    }
    
    setStats(statsData);
  };

  const handleSaveTour = async (tourData: Partial<Tour>) => {
    setLoading(true);
    try {
      // Validar campos obrigatórios
      if (!tourData.name || !tourData.page) {
        toast({
          title: 'Erro',
          description: 'Nome e página são obrigatórios.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Converter para o formato do banco
      const dbData: any = {
        name: tourData.name,
        page: tourData.page,
        description: tourData.description || '',
        is_active: tourData.is_active ?? true,
        auto_start: tourData.auto_start ?? true,
        show_progress: tourData.show_progress ?? true,
        continuous: tourData.continuous ?? true,
        show_skip_button: tourData.show_skip_button ?? true,
        steps: tourData.steps || [],
      };

      if (editingTour) {
        // Update
        const { error } = await supabase
          .from('tours')
          .update(dbData)
          .eq('id', editingTour.id);

        if (error) throw error;
        toast({ title: 'Tour atualizado com sucesso!' });
      } else {
        // Create
        const { error } = await supabase
          .from('tours')
          .insert([dbData]);

        if (error) throw error;
        toast({ title: 'Tour criado com sucesso!' });
      }

      setIsCreateOpen(false);
      setEditingTour(null);
      loadTours();
    } catch (error) {
      console.error('Erro ao salvar tour:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar o tour.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTour = async (tourId: string) => {
    if (!confirm('Tem certeza que deseja excluir este tour?')) return;

    try {
      const { error } = await supabase
        .from('tours')
        .delete()
        .eq('id', tourId);

      if (error) throw error;
      toast({ title: 'Tour excluído com sucesso!' });
      loadTours();
    } catch (error) {
      console.error('Erro ao excluir tour:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o tour.',
        variant: 'destructive',
      });
    }
  };

  const handleDuplicateTour = async (tour: Tour) => {
    const duplicate: Partial<Tour> = {
      name: `${tour.name} (Cópia)`,
      description: tour.description,
      page: tour.page,
      steps: tour.steps,
      is_active: false,
      auto_start: tour.auto_start,
      show_progress: tour.show_progress,
      continuous: tour.continuous,
      show_skip_button: tour.show_skip_button,
    };

    await handleSaveTour(duplicate);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Tours Interativos</CardTitle>
            <CardDescription>
              Gerencie tours guiados para ajudar usuários a navegar pelo sistema
            </CardDescription>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingTour(null)}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Tour
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingTour ? 'Editar Tour' : 'Criar Novo Tour'}
                </DialogTitle>
              </DialogHeader>
              <TourForm
                tour={editingTour}
                pages={pages}
                onSave={handleSaveTour}
                onCancel={() => {
                  setIsCreateOpen(false);
                  setEditingTour(null);
                }}
                loading={loading}
              />
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {tours.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum tour cadastrado ainda.</p>
              <p className="text-sm mt-2">Clique em "Novo Tour" para começar.</p>
            </div>
          ) : (
            tours.map((tour) => (
              <div
                key={tour.id}
                className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold flex items-center gap-2 flex-wrap">
                      {tour.name}
                      {tour.is_active ? (
                        <Badge variant="default">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                      {tour.auto_start && (
                        <Badge variant="outline">Auto-start</Badge>
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {tour.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Página: <code className="bg-muted px-1 rounded">{tour.page}</code></span>
                      <span>• {tour.steps.length} passos</span>
                      <span className="flex items-center gap-1">
                        • <Users className="h-3 w-3" /> {stats[tour.id] || 0} completaram
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDuplicateTour(tour)}
                      title="Duplicar tour"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingTour(tour);
                        setIsCreateOpen(true);
                      }}
                      title="Editar tour"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteTour(tour.id)}
                      title="Excluir tour"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ToursTab;
